import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AuthRepository } from "../repositories/AuthRepository";
import { useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { setCurrentUser } from "../../store/AuthSlice";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";
import { CapWidget } from "@pitininja/cap-react-widget";
import { SETTINGS } from "../../Settings";
import { Container } from "@mui/system";

export default function Login() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
  });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [widgetKey, setWidgetKey] = useState(0);

  const handleChange = (name, value) => {
    setLoginData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogin = () => {
    setError();
    setLoading(true);
    AuthRepository.fetchToken(
      loginData.username,
      loginData.password,
      captchaToken,
    )
      .then((response) => {
        setLoading(false);
        if (response) {
          const user = jwtDecode(response.data.accessToken);
          localStorage.setItem("accessToken", response.data.accessToken);
          dispatch(setCurrentUser(user));
          navigate("/");
        }
      })
      .catch((error) => {
        resetCaptcha();
        setError(ErrorMessageResolver.resolve(error));
        setLoading(false);
      });
  };

  const resetCaptcha = () => {
    setWidgetKey((prev) => prev + 1);
    setCaptchaToken(null);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box className="login-container">
      <Container
        className="login-container-inner"
        sx={{ marginTop: { md: "-150px", sm: "-50px", xs: "80px" } }}
      >
        <Grid item xs={12} className="login-logo-container">
          <Hidden mdUp>
            <img
              id="rm-coat-of-arms"
              src={`${process.env.PUBLIC_URL}/assets/mt-logo.png`}
              alt="RM"
              width="50"
            />
          </Hidden>
          <Hidden mdDown>
            <img
              id="rm-coat-of-arms"
              src={`${process.env.PUBLIC_URL}/assets/mt-logo.png`}
              alt="RM"
              width="75"
            />
          </Hidden>
          <Typography variant={"body1"} sx={{ color: "white", mb: 1 }}>
            МИНИСТЕРСТВО ЗА ТРАНСПОРТ
          </Typography>
          <Typography variant={"body1"} sx={{ color: "white" }}>
            Систем за електронско аплицирање за трајна и времена измена на режим
            на сообраќај
          </Typography>
        </Grid>

        <Grid item xs={12} className="login-inner-grid">
          <Typography className="login-heading">Најава</Typography>
          {error && (
            <Box sx={{ padding: "20px 40px 0" }}>
              <Alert severity="error" variant="filled" sx={{ mb: 0 }}>
                <Typography>{error}</Typography>
              </Alert>
            </Box>
          )}
          <Grid container className="login-inputs-container">
            <Grid item xs={12} md={12} sx={{ mb: 2 }}>
              <TextField
                size="medium"
                fullWidth
                placeholder="Корисничко име"
                autoComplete="off"
                inputProps={{ spellCheck: "false" }}
                onKeyDown={(e) => handleKeyDown(e)}
                onChange={(e) => handleChange("username", e.target.value)}
                value={loginData?.username || ""}
                className="login-textfield"
              />
            </Grid>
            <Grid item xs={12} md={12} sx={{ mb: 2 }}>
              <TextField
                size="medium"
                fullWidth
                type={showPassword ? "text" : "password"}
                placeholder="Лозинка"
                autoComplete="off"
                inputProps={{ spellCheck: "false" }}
                onChange={(e) => handleChange("password", e.target.value)}
                value={loginData?.password || ""}
                className="login-textfield"
                onKeyDown={(e) => handleKeyDown(e)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleShowPassword();
                        }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") {
                            toggleShowPassword();
                          }
                        }}
                      >
                        {showPassword ? (
                          <FaRegEye className="login-show-hide-password-icon" />
                        ) : (
                          <FaRegEyeSlash className="login-show-hide-password-icon" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3.5} sx={{ mt: 1 }}></Grid>
            <Grid item xs={12} md={5} sx={{ mt: 1, textAlign: "center" }}>
              <CapWidget
                key={widgetKey}
                endpoint={SETTINGS.CAPTCHA_API_URL}
                onSolve={(token) => {
                  console.log(`Challenge succeeded, token : ${token}`);
                  setCaptchaToken(token);
                }}
                onError={(err) => {
                  console.log(`Challenge failed`);
                  setCaptchaToken(null);
                }}
                customWaspUrl={`${window.location.origin}/cap/cap_wasm.js`}
                className="captcha1"
                i18nInitial="Верификувај ме"
                i18nVerifying="Се верификува..."
                i18nSolved="Верификуван"
                i18nError="Настана грешка"
                i18nVerifyAriaLabel="Кликни за да се верификуваш"
                i18nVerifyingAriaLabel="Се верификува"
                i18nVerifiedAriaLabel="Успешно верификуван"
                i18nErrorAriaLabel="Грешка при верификација"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <Typography variant="h6" id="login-forgot-pass-text">
                &nbsp;
              </Typography>
              <Button
                variant="contained"
                fullWidth
                color="primary"
                disabled={loading || !captchaToken}
                size="large"
                onClick={() => handleLogin()}
              >
                {!loading ? (
                  <Typography className="login-button-text">
                    Најави се
                  </Typography>
                ) : (
                  <CircularProgress size={20} id="circular-progress" />
                )}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
